/**
 * Centralized Slot Prop Normalizers
 *
 * Maps raw slot payload (from AEM Content Fragments or Adobe Target/Alloy DCA)
 * into component-specific props. Each component has its own normalizer that
 * handles both CF and DCA data structures. Each normalizer derives what it
 * needs from the combined slot and trackingCtx.
 */
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import type { TrackingPropsInterface } from '@marriott/mi-ui-library';
import { AspectRatio, SmartCropFromAspectRatio } from '@marriott/mi-web-ui-kit';
import type { ProductDetailsCardProps } from '../organisms/ProductDetailsCard/ProductDetailsCard.types';
import type { EnrollmentCardContainerProps } from '../organisms/EnrollmentCardContainer/EnrollmentCardContainer.types';
import type {
  SlotContent,
  SlotMeta,
  SlotResolveContext,
} from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getTrackingPropertiesDynamic } from '@marriott/mi-merch-components';
import {
  adaptContentFragmentToLayeredFeature,
  adaptDcaLayeredFeature,
  type LayeredFeatureCfPayload,
  type LayeredFeatureDCAPayload,
} from './adapters';
import { buildDynamicMediaNode, ASPECT_RATIOS } from './dynamicMediaUtils';
import { getRecentlyViewedImageFallbackForPayload } from './recentlyViewedUtils';
import { isRecord } from './helper';
import { EMPTY_RENDITIONS, getBaseUrlAndAltFromPayload, resolveBrandLogoIcon, resolveImageUrl } from './imageUtils';
import { resolveMerchandisingCategory, trackingProps } from './polarisTrackingUtils';
const imageDomain = useClientEnvVarsStore.getState().envVarsObject['DAC_IMG_DOMAIN'] || 'https://cache.marriott.com';

function getContentObj(slot: SlotContent): Record<string, unknown> {
  return (slot?.payload && typeof slot?.payload === 'object' ? slot.payload : {}) as Record<string, unknown>;
}

function resolveDcaMboxParameter(ctx: SlotResolveContext, c: Record<string, unknown>): string {
  if (typeof c['mboxName'] === 'string' && c['mboxName']) return c['mboxName'];
  if (typeof c['mboxParameter'] === 'string' && c['mboxParameter']) return c['mboxParameter'];
  return ctx.slot?.dcaTarget?.mboxParameter ?? '';
}

// ─── ProductDetailsCard ────────────────────────────────────────────────────
const PDC_DCA_IMAGE_URLS_BY_ASPECT_RATIO = [
  { aspectRatio: ASPECT_RATIOS.CLASSIC_HOR, imageUrlKey: 'classicHorizontal', smartCrop: 'Classic-Hor' },
  { aspectRatio: ASPECT_RATIOS.SQUARE, imageUrlKey: 'square', smartCrop: 'Square' },
  { aspectRatio: ASPECT_RATIOS.WIDE_HOR, imageUrlKey: 'wideHorizontal', smartCrop: 'Wide-Hor' },
  { aspectRatio: ASPECT_RATIOS.CLASSIC_VER, imageUrlKey: 'classicVertical', smartCrop: 'Classic-Ver' },
] as const;

function stripSmartCropSuffix(url: string, smartCrop: string): string {
  const queryStart = url.indexOf('?');
  const path = queryStart >= 0 ? url.slice(0, queryStart) : url;
  const query = queryStart >= 0 ? url.slice(queryStart) : '';
  const suffix = `:${smartCrop}`;
  return path.endsWith(suffix) ? `${path.slice(0, -suffix.length)}${query}` : url;
}

function getDcaPdcImageUrl(
  c: Record<string, unknown>,
  aspectRatio: AspectRatio
): { defaultUrl: string; dynamicBaseUrl: string } {
  const image = isRecord(c['image']) ? c['image'] : null;
  const imageUrls = image && isRecord(image['imageUrls']) ? image['imageUrls'] : null;
  const config = PDC_DCA_IMAGE_URLS_BY_ASPECT_RATIO.find(item => item.aspectRatio === aspectRatio);
  if (!config) return { defaultUrl: '', dynamicBaseUrl: '' };

  const rawUrl = typeof imageUrls?.[config.imageUrlKey] === 'string' ? imageUrls[config.imageUrlKey] : '';
  if (!rawUrl || typeof rawUrl !== 'string') return { defaultUrl: '', dynamicBaseUrl: '' };

  const defaultUrl = resolveImageUrl(rawUrl);
  return {
    defaultUrl,
    dynamicBaseUrl: stripSmartCropSuffix(defaultUrl, config.smartCrop),
  };
}

function getPendingDcaTrackingProps(baseTrackingProps: TrackingPropsInterface): TrackingPropsInterface {
  return {
    ...baseTrackingProps,
    trackingContentPosition: '',
    trackingDescription: '',
    trackingOfferType: '',
    trackingTag: '',
    atCCeVar48: '',
    clickTrack: false,
    impressionTrack: false,
    impressionCount: false,
  };
}

function getMetadataRecord(contentObj: Record<string, unknown>): Record<string, unknown> | undefined {
  const metadata = contentObj['metaData'] ?? contentObj['metadata'];
  return isRecord(metadata) ? metadata : undefined;
}

export function normalizeProductDetailsCardProps(ctx: SlotResolveContext): ProductDetailsCardProps {
  const c = getContentObj(ctx.slot);
  const isDcaSlot = ctx.slot?.type === 'dca';
  // Slot-type agnostic "awaiting async content" flag. Any slot that reserves a
  // position and loads its content later (e.g. geo-cf today) sets `isLoading` so
  // it inherits the same skeleton without a new card prop.
  const isSlotLoading = ctx.slot?.isLoading === true;
  const size = (ctx.slot?.size as SlotMeta['size'] | 'wide') ?? 'square';
  const dcaSquareImage = isDcaSlot ? getDcaPdcImageUrl(c, ASPECT_RATIOS.SQUARE) : null;
  const dcaWideImage = isDcaSlot ? getDcaPdcImageUrl(c, ASPECT_RATIOS.WIDE_HOR) : null;
  const dcaTallImage = isDcaSlot ? getDcaPdcImageUrl(c, ASPECT_RATIOS.CLASSIC_VER) : null;
  const { baseUrl: extractedBaseUrl, alt } = getBaseUrlAndAltFromPayload(c);
  const baseUrl =
    dcaSquareImage?.dynamicBaseUrl ||
    extractedBaseUrl ||
    (isDcaSlot ? getRecentlyViewedImageFallbackForPayload(c, 'square') : '');
  let resolvedBaseUrl = resolveImageUrl(baseUrl);
  if (resolvedBaseUrl?.indexOf('.tif') > -1) {
    let newBaseUrl = '';
    [ASPECT_RATIOS.SQUARE, ASPECT_RATIOS.WIDE_HOR, ASPECT_RATIOS.CLASSIC_VER].map((smartCropType: AspectRatio) => {
      const cropType = smartCropType ? SmartCropFromAspectRatio.get(smartCropType) : undefined;
      const renditionUrl = cropType
        ? (c['image'] as { imageUrls?: Record<string, string>; imageSrc?: string })?.['imageUrls']?.[
            cropType.toLowerCase()
          ]
        : (c['image'] as { imageUrls?: Record<string, string>; imageSrc?: string })?.imageSrc;
      newBaseUrl = newBaseUrl || imageDomain + renditionUrl;
    });
    resolvedBaseUrl = newBaseUrl;
  }
  const squareImageUrl = dcaSquareImage?.dynamicBaseUrl || resolvedBaseUrl;
  const wideImageUrl = dcaWideImage?.dynamicBaseUrl || resolvedBaseUrl;
  const tallImageUrl = dcaTallImage?.dynamicBaseUrl || resolvedBaseUrl;
  const defaultImageUrl =
    size === 'tall'
      ? dcaTallImage?.defaultUrl || resolvedBaseUrl
      : size === 'wide'
      ? dcaWideImage?.defaultUrl || resolvedBaseUrl
      : dcaSquareImage?.defaultUrl || resolvedBaseUrl;
  const promotionalTag = typeof c['promotionalTag'] === 'string' ? c['promotionalTag'] : '';
  const badgeText = (promotionalTag || c['badgeText'] || '') as string;
  const promotionalTagIconMap: Record<string, string> = {
    'member exclusive': 'marriott-icons:icon-lock',
    'limited time offer': 'marriott-icons:icon-alarm',
    'new promotion': 'marriott-icons:icon-promotion-achieved',
    'recently viewed': 'marriott-icons:icon-clock',
  };
  const promotionalTagKey = promotionalTag.trim().toLowerCase();
  let customBadgeIcon = '';
  if (typeof c['badgeIcon'] === 'string') {
    customBadgeIcon = c['badgeIcon'];
  } else if (Array.isArray(c['badgeIcon']) && typeof c['badgeIcon'][0] === 'string') {
    customBadgeIcon = c['badgeIcon'][0];
  }

  let badgeIcon = '';

  if (promotionalTag) {
    badgeIcon = promotionalTagIconMap[promotionalTagKey] ?? customBadgeIcon;
  } else {
    badgeIcon = customBadgeIcon;
  }
  const idx = ctx.trackingCtx?.index ?? 0;

  const baseTrackingProps = trackingProps(c, ctx.trackingCtx, ctx.slot?.type, ctx.slot) as TrackingPropsInterface;
  const metadataRecord = getMetadataRecord(c);
  const metaData = metadataRecord as Parameters<typeof getTrackingPropertiesDynamic>[1] | undefined;
  const tp = isDcaSlot
    ? metaData
      ? (getTrackingPropertiesDynamic(
          baseTrackingProps as Parameters<typeof getTrackingPropertiesDynamic>[0],
          metaData,
          { ...c, metaData } as Parameters<typeof getTrackingPropertiesDynamic>[2],
          Boolean((baseTrackingProps as Record<string, unknown>)['isAuth']),
          c['isTargetContentError'] === true,
          resolveDcaMboxParameter(ctx, c)
        ) as unknown as TrackingPropsInterface)
      : getPendingDcaTrackingProps(baseTrackingProps)
    : baseTrackingProps;
  const merchandisingCategory = resolveMerchandisingCategory(c, isDcaSlot, metadataRecord);
  const pdcTrackingProperties: TrackingPropsInterface = {
    ...tp,
    merchandisingCategory,
  };
  if (isDcaSlot && metaData) {
    pdcTrackingProperties.clickTrackValSeparator = pdcTrackingProperties.clickTrackValSeparator || ';';
  }

  return {
    ...c,
    badgeText,
    badgeIcon,
    headerLong: c['headerLong'] || c['header'] || '',
    headerMedium: c['headerMedium'] || c['header'] || '',
    headerShort: c['headerShort'] || c['header'] || '',
    descriptionLong: c['descriptionLong'] || c['description'] || '',
    descriptionMedium: c['descriptionMedium'] || c['description'] || '',
    descriptionShort: c['descriptionShort'] || c['description'] || '',
    title: (c['title'] || c['header'] || c['headerLong'] || c['headerMedium'] || c['headerShort'] || '') as string,
    eyebrow: c['eyebrowText'] || ((c['eyebrow'] || '') as string),
    description: (c['description'] ||
      c['descriptionLong'] ||
      c['descriptionMedium'] ||
      c['descriptionShort'] ||
      '') as string,
    fileReferenceImage: defaultImageUrl,
    breakPointKey: 'viewportL',
    cardVariation: size,
    index: idx,
    trackingProperties: pdcTrackingProperties,
    cardUrl: c['destinationUrl1'] || c['cardUrl'] || '#',
    openInaNewTab: c['openInaNewTab'] === true,
    brandLogoIcon: resolveBrandLogoIcon(c),
    imageAltText: c['imageAltText'] || alt || '',
    contentType: c['contentType'],
    dynamicMedia: buildDynamicMediaNode(squareImageUrl, ASPECT_RATIOS.SQUARE, alt) ?? EMPTY_RENDITIONS,
    dynamicMediaWide: buildDynamicMediaNode(wideImageUrl, ASPECT_RATIOS.WIDE_HOR, alt) ?? EMPTY_RENDITIONS,
    dynamicMediaTall: buildDynamicMediaNode(tallImageUrl, ASPECT_RATIOS.CLASSIC_VER, alt) ?? EMPTY_RENDITIONS,
    isSlotLoading,
  } as unknown as ProductDetailsCardProps;
}

// ─── EnrollmentCard (CF-only; logged-in/out resolution handled by EnrollmentCardContainerComp) ───
export function normalizeEnrollmentCardProps(ctx: SlotResolveContext): EnrollmentCardContainerProps {
  const c = getContentObj(ctx.slot);
  const { baseUrl, alt } = getBaseUrlAndAltFromPayload(c);
  const fileReferenceImage = baseUrl;
  const tp = trackingProps(c, ctx.trackingCtx, ctx.slot?.type, ctx.slot);
  return {
    ...c,
    primaryCtaLinkText: c['primaryCtaLinkText'] || '',
    primaryCtaLink: c['primaryCtaLink'] || '',
    primaryCtaOpenInNewTab: c['primaryCtaOpenInNewTab'] === true || c['primaryCtaOpenInNewTab'] === 'true',
    secondaryCtaLink: c['secondaryCtaLink'] || '',
    secondaryCtaLinkText: c['secondaryCtaLinkText'] || '',
    secondaryCtaOpenInNewTab: c['secondaryCtaOpenInNewTab'] === true || c['secondaryCtaOpenInNewTab'] === 'true',
    enrollmentCardDescription: c['enrollmentCardDescription'] || c['description'] || '',
    // Pass through logged-in / logged-out variant fields for the component to resolve
    headerLoggedIn: c['headerLoggedIn'] || '',
    headerLoggedOut: c['headerLoggedOut'] || '',
    descriptionLoggedIn: c['descriptionLoggedIn'] || '',
    descriptionLoggedOut: c['descriptionLoggedOut'] || '',
    ctaLinkLoggedIn: c['ctaLinkLoggedIn'] || '',
    ctaLinkTextLoggedIn: c['ctaLinkTextLoggedIn'] || '',
    ctaOpenInNewTabLoggedIn: c['ctaOpenInNewTabLoggedIn'] === true || c['ctaOpenInNewTabLoggedIn'] === 'true',
    theme: c['theme'] || 'standard',
    altText: c['imageAltText'] || alt,
    fileReferenceImage,
    dynamicMedia: buildDynamicMediaNode(baseUrl, ASPECT_RATIOS.CLASSIC_HOR, alt) ?? EMPTY_RENDITIONS,
    trackingProperties: tp,
  } as unknown as EnrollmentCardContainerProps;
}

// ─── MerchandisingCoBrandCard ───────────────────────────────────────────────
export function normalizeMerchandisingCoBrandCardProps(ctx: SlotResolveContext): Record<string, unknown> {
  const c = getContentObj(ctx.slot);
  const { baseUrl, alt } = getBaseUrlAndAltFromPayload(c, 'logo');
  const image = buildDynamicMediaNode(baseUrl, undefined, alt) ?? EMPTY_RENDITIONS;
  const tp = trackingProps(c, ctx.trackingCtx, ctx.slot?.type, ctx.slot);
  const openInNewTab = c['openInNewTab'] || c['primaryCtaOpenInNewTab'] || false;

  const metadata = getMetadataRecord(c);
  const evar48 = ((c['atCCeVar48'] || c['eVar48'] || metadata?.['evar48'] || '') as string).trim();
  const isDcaSlot = ctx.slot?.type === 'dca';
  const merchandisingCategory = resolveMerchandisingCategory(c, isDcaSlot, metadata, 'cobrandNonPointsMath');
  return {
    ...c,
    headerLong: c['headerLong'] || c['header'] || '',
    headerMedium: c['headerMedium'] || c['header'] || '',
    descriptionLong: c['descriptionLong'] || c['description'] || '',
    descriptionMedium: c['descriptionMedium'] || c['description'] || '',
    callToActionText1: c['callToActionText1'] || c['primaryCTALinkText'] || '',
    destinationUrl1: c['destinationUrl1'] || c['primaryCTALink'] || '',
    callToActionText2: c['callToActionText2'] || c['primaryCTALinkText'] || '',
    destinationUrl2: c['destinationUrl2'] || c['primaryCTALink'] || '',
    openInNewTab,
    image,
    mboxParameter: isDcaSlot ? resolveDcaMboxParameter(ctx, c) : '',
    trackingProperties: {
      ...tp,
      atCCeVar48: evar48 || resolveDcaMboxParameter(ctx, c),
      isCoBrand: true,
      type: 'CoBrand',
      merchandisingCategory,
      isDCAEnabled: isDcaSlot,
      trackingTag: metadata?.['merTag'] || undefined,
    },
    styleClass: c['styleClass'] || c['styleclass'] || '',
    isSquare: c['size'] === 'square',
    // Omitted: treat as loaded; explicit false from Target or resolveSlot synthetic placeholder shows skeleton.
    isTargetContentLoaded: (c['isTargetContentLoaded'] as boolean | undefined) ?? true,
  };
}

// ─── RiottMediaCard ───────────────────────────────────────────────────────
export function normalizeRiottMediaCardProps(ctx: SlotResolveContext): Record<string, unknown> {
  const c = getContentObj(ctx.slot);
  const tp = trackingProps(c, ctx.trackingCtx, ctx.slot?.type, ctx.slot);
  const isDcaSlot = ctx.slot?.type === 'dca';

  const metadata = getMetadataRecord(c);
  const evar48 = (
    (metadata?.['evar48'] || metadata?.['eVar48'] || c['atCCeVar48'] || c['eVar48'] || '') as string
  ).trim();
  const mboxParameter = (c['mboxName'] || c['mboxParameter']) as string | undefined;
  const isMango = metadata?.['isMango'] === true;
  //Mango cards are authored as `cobrandNonPointsMath` so the shared Button treats them as cobrand and fires the `evar48=<mbox>_adLoaded` impression.
  const authoredTileCategory = ctx.slot?.dcaTarget?.merchandisingCategory;
  const merchandisingCategory = resolveMerchandisingCategory(
    c,
    isDcaSlot,
    metadata,
    authoredTileCategory || 'nonCobrand'
  );
  const riottAtCCeVar48 =
    evar48 || (isMango && typeof mboxParameter === 'string' && mboxParameter ? `${mboxParameter}_adLoaded` : '');
  const riottTrackingDescription =
    (isMango ? 'Mango' : '') ||
    evar48 ||
    ((metadata?.['title'] || c['trackingDescription'] || c['title'] || mboxParameter || '') as string).trim();
  const riottTrackingPosition =
    (typeof mboxParameter === 'string' && mboxParameter ? mboxParameter.replace(/-mbox$/i, '') : '') ||
    ((tp.trackingContentPosition === 'dca' ? '' : tp.trackingContentPosition) as string | undefined);
  const firstAd = Array.isArray(c['adData']) ? (c['adData'][0] as Record<string, unknown> | undefined) : undefined;
  const assets = firstAd?.['assets'] as Record<string, unknown> | undefined;
  const hasRiottAdAssets = Boolean(
    assets?.['primaryImage_desktop'] || assets?.['primaryImage_tablet'] || assets?.['primaryImage_mobile']
  );
  const hasRiottLoadedTrackingUrl =
    typeof firstAd?.['custom_tracking_url'] === 'string' && firstAd['custom_tracking_url'];
  const isRiottTargetContentLoaded =
    isMango && c['isTargetContentLoaded'] !== false
      ? hasRiottAdAssets && Boolean(hasRiottLoadedTrackingUrl)
      : (c['isTargetContentLoaded'] as boolean | undefined) ?? true;

  return {
    ...c,
    displayEvergreenContent: false,
    adData: (c['adData'] as unknown[]) ?? [],
    mboxParameter,
    isTargetContentLoaded: isRiottTargetContentLoaded,
    isTargetContentError: c['isTargetContentError'] || false,
    trackingProperties: {
      ...tp,
      merchandisingCategory,
      ...(riottAtCCeVar48 && { atCCeVar48: riottAtCCeVar48 }),
      ...(riottTrackingPosition && { trackingContentPosition: riottTrackingPosition }),
      ...(riottTrackingDescription && { trackingDescription: riottTrackingDescription }),
      ...(riottAtCCeVar48 && {
        isDCAEnabled: 'true',
      }),
    },
    styleclass: `riott-media_border_radius ${c['styleClass'] || c['styleclass'] || ''}`.trim() as string,
    metaData: metadata,
  };
}

// ─── LayeredFeatureCard ───────────────────────────────────────────────────────
export function normalizeLayeredFeatureCardProps(ctx: SlotResolveContext) {
  const contentObj = getContentObj(ctx.slot);
  const { baseUrl, alt } = getBaseUrlAndAltFromPayload(contentObj);
  const normalizedContext = {
    slot: ctx.slot,
    contentObj,
    baseUrl,
    alt,
    trackingCtx: ctx.trackingCtx,
  };

  const adapted =
    ctx.slot?.type === 'dca'
      ? adaptDcaLayeredFeature(normalizedContext as LayeredFeatureDCAPayload)
      : adaptContentFragmentToLayeredFeature(normalizedContext as LayeredFeatureCfPayload);

  const metadataRecord = getMetadataRecord(contentObj);
  const metadata = metadataRecord as Parameters<typeof getTrackingPropertiesDynamic>[1] | undefined;
  const isDcaSlot = ctx.slot?.type === 'dca';
  const baseTrackingProps = trackingProps(
    contentObj,
    ctx.trackingCtx,
    ctx.slot?.type,
    ctx.slot
  ) as TrackingPropsInterface;
  const evar48 = (
    (contentObj['atCCeVar48'] || contentObj['eVar48'] || metadataRecord?.['evar48'] || '') as string
  ).trim();

  const trackingProperties: TrackingPropsInterface = isDcaSlot
    ? metadata
      ? (getTrackingPropertiesDynamic(
          baseTrackingProps as Parameters<typeof getTrackingPropertiesDynamic>[0],
          metadata,
          { ...contentObj, metaData: metadata } as Parameters<typeof getTrackingPropertiesDynamic>[2],
          Boolean((baseTrackingProps as Record<string, unknown>)['isAuth']),
          contentObj['isTargetContentError'] === true,
          resolveDcaMboxParameter(ctx, contentObj)
        ) as unknown as TrackingPropsInterface)
      : getPendingDcaTrackingProps(baseTrackingProps)
    : {
        ...baseTrackingProps,
        ...(evar48 ? { atCCeVar48: evar48 } : {}),
      };

  const merchandisingCategory = resolveMerchandisingCategory(contentObj, isDcaSlot, metadataRecord);
  const lfcTrackingProperties: TrackingPropsInterface = {
    ...trackingProperties,
    merchandisingCategory,
  };
  if (isDcaSlot && metadata) {
    lfcTrackingProperties.clickTrackValSeparator = lfcTrackingProperties.clickTrackValSeparator || ';';
  }

  return {
    ...contentObj,
    ...adapted,
    trackingProperties: lfcTrackingProperties,
  };
}
